import { useState } from "react";
import { CopyCode } from "@/components/CopyCode";
import { useQuery } from "@tanstack/react-query";
import { Wifi, WifiOff, Store } from "lucide-react";
import { api } from "@/lib/api";
import { formatBs, formatUsd, formatCop, formatTime, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FMT = { BS: formatBs, USD: formatUsd, COP: formatCop };
const money = (cur, v) => (FMT[cur] || formatBs)(v || 0);
const todayCcs = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Caracas" });
const STATUS = { active: "Activo", winner: "Por pagar", paid: "Pagado", lost: "Perdedor", void: "Anulado", pending_payment: "Pago pendiente" };

const useCaja00 = (params) => useQuery({
  queryKey: ["caja-00", params], refetchInterval: params ? false : 15000,
  queryFn: async () => (await api.get("/reports/caja-00", { params })).data,
});

function Header({ data, online, title }) {
  const st = data?.agencia?.state;
  return (
    <div className="flex flex-wrap items-end justify-between gap-3" data-testid="caja00-header">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">Caja 00 · Taquilla Madre</div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight" data-testid="caja00-agencia-name">{data?.agencia?.name || "…"}</h1>
        <p className="text-sm text-muted-foreground">{title} · <span className="font-mono">{data?.agencia?.code}</span> · <span data-testid="caja00-agencia-state" className="capitalize">{st}</span></p>
      </div>
      <span data-testid="caja00-connection" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${online ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10" : "border-rose-500/40 text-rose-600 bg-rose-500/10"}`}>
        {online ? <Wifi size={13} /> : <WifiOff size={13} />}{online ? "En línea" : "Sin conexión"}</span>
    </div>
  );
}

function KpiBlock({ cur, k }) {
  const items = [["Ventas Totales", k.ventas, "ventas"], ["Premios Pagados", k.pagados, "pagados"], ["Premios por Pagar", k.por_pagar, "por-pagar"], ["Balance Neto", k.balance, "balance"]];
  return (
    <Card className="fintech-card p-4 space-y-3" data-testid={`caja00-kpi-${cur}`}>
      <div className="flex items-center justify-between"><span className="text-xs font-bold tracking-widest text-primary">{cur}</span>
        <span className="text-xs text-muted-foreground">{k.tickets} {k.tickets === 1 ? "ticket" : "tickets"}</span></div>
      <div className="grid grid-cols-2 gap-3">{items.map(([l, v, t]) => (
        <div key={t}><div className="text-[11px] text-muted-foreground">{l}</div>
          <div className={`font-display text-lg font-semibold tabular-nums ${t === "balance" && v < 0 ? "text-rose-500" : ""}`} data-testid={`caja00-kpi-${cur}-${t}`}>{money(cur, v)}</div></div>))}</div>
    </Card>
  );
}

function TaquillasTable({ data }) {
  const curs = data.agencia.currencies;
  return (
    <Card className="fintech-card overflow-hidden"><div className="overflow-x-auto">
      <table className="w-full text-sm fintech-table [&_th]:px-3 [&_td]:px-3" data-testid="caja00-taquillas-table">
        <thead><tr><th className="text-left">Código</th><th className="text-left">Taquillera</th><th className="text-left">Caja</th><th className="text-right">Ventas del turno</th><th className="text-right">Última actividad</th></tr></thead>
        <tbody>{data.taquillas.map((t) => (
          <tr key={t.id} data-testid={`caja00-taquilla-row-${t.code}`}>
            <td className="font-mono">{t.code}<div className="text-xs text-muted-foreground font-sans">{t.name}</div></td>
            <td>{t.operadores || <span className="text-muted-foreground">Sin asignar</span>}</td>
            <td><span data-testid={`caja00-cash-status-${t.code}`} className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${t.cash_status === "open" ? "border-emerald-600/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10" : "border-border text-muted-foreground"}`}>{t.cash_status === "open" ? "Abierta" : "Cerrada"}</span></td>
            <td className="text-right tabular-nums">{t.turno ? curs.map((c) => <div key={c} data-testid={`caja00-turno-${t.code}-${c}`}>{money(c, t.turno[c])} <span className="text-[10px] text-muted-foreground">{c}</span></div>) : "—"}</td>
            <td className="text-right text-xs text-muted-foreground">{t.last_activity ? formatDate(t.last_activity) : "Sin actividad"}</td>
          </tr>))}
          {!data.taquillas.length && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">La agencia no tiene taquillas</td></tr>}</tbody>
      </table></div></Card>
  );
}

function Consolidado() {
  const { data, isLoading, isError } = useCaja00(null);
  if (isLoading) return <Skeleton className="h-72 w-full" />;
  if (!data) return <div className="text-sm text-muted-foreground">No se pudo cargar el consolidado.</div>;
  return (
    <div className="space-y-5" data-testid="caja00-consolidado">
      <Header data={data} online={!isError} title="Consolidado del día" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{data.agencia.currencies.map((c) => <KpiBlock key={c} cur={c} k={data.kpis[c]} />)}</div>
      <TaquillasTable data={data} />
    </div>
  );
}

function RangeFilter({ range, setRange, extra }) {
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="caja00-range-filter">
      <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={range.date_from} max={range.date_to} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="caja00-date-from" /></div>
      <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="date" value={range.date_to} min={range.date_from} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="caja00-date-to" /></div>
      {extra}
    </div>
  );
}

function Historial() {
  const [range, setRange] = useState({ date_from: todayCcs(), date_to: todayCcs() });
  const [cur, setCur] = useState("BS");
  const { data: c0, isError } = useCaja00(null);
  const taqMap = Object.fromEntries((c0?.taquillas || []).map((t) => [t.id, t]));
  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["caja00-tickets", range, cur],
    queryFn: async () => (await api.get("/tickets", { params: { ...range, currency: cur, limit: 500 } })).data,
  });
  const field = cur === "USD" ? "usd" : cur === "COP" ? "cop" : "bs";
  return (
    <div className="space-y-5" data-testid="caja00-historial">
      <Header data={c0} online={!isError} title="Historial de Tickets" />
      <RangeFilter range={range} setRange={setRange} extra={
        <div className="space-y-1"><Label className="text-xs">Moneda</Label>
          <Select value={cur} onValueChange={setCur}><SelectTrigger className="w-28" data-testid="caja00-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(c0?.agencia?.currencies || ["BS"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>} />
      <Card className="fintech-card overflow-hidden"><div className="overflow-x-auto">
        <table className="w-full text-sm fintech-table [&_th]:px-3 [&_td]:px-3" data-testid="caja00-tickets-table">
          <thead><tr><th className="text-left">Ticket</th><th className="text-left">Taquilla</th><th className="text-left">Hora</th><th className="text-right">Monto</th><th className="text-right">Premio</th><th className="text-left">Estatus</th></tr></thead>
          <tbody>{isLoading ? <tr><td colSpan={6}><Skeleton className="h-10 w-full" /></td></tr> : tickets.map((t) => (
            <tr key={t.id} data-testid={`caja00-ticket-row-${t.code}`}>
              <td className="font-mono"><CopyCode code={t.code} /></td><td>{taqMap[t.taquilla_id]?.code || "—"}</td><td className="text-xs">{formatTime(t.created_at)}</td>
              <td className="text-right tabular-nums">{money(cur, t[`total_${field}`])}</td>
              <td className="text-right tabular-nums">{t[`payout_total_${field}`] ? money(cur, t[`payout_total_${field}`]) : "—"}</td>
              <td>{STATUS[t.status] || t.status}</td></tr>))}
            {!isLoading && !tickets.length && <tr><td colSpan={6} className="text-center text-muted-foreground py-6" data-testid="caja00-tickets-empty">Sin tickets en el rango</td></tr>}</tbody>
        </table></div></Card>
    </div>
  );
}

function Cuadre() {
  const [range, setRange] = useState({ date_from: todayCcs(), date_to: todayCcs() });
  const { data, isLoading, isError } = useCaja00(range);
  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;
  const curs = data.agencia.currencies;
  return (
    <div className="space-y-5" data-testid="caja00-cuadre">
      <Header data={data} online={!isError} title="Reportes de Cuadre" />
      <RangeFilter range={range} setRange={setRange} />
      {curs.map((c) => (
        <Card key={c} className="fintech-card overflow-hidden" data-testid={`caja00-cuadre-${c}`}>
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2"><Store size={15} className="text-primary" /><span className="text-xs font-bold tracking-widest text-primary">{c}</span></div>
          <div className="overflow-x-auto"><table className="w-full text-sm fintech-table [&_th]:px-3 [&_td]:px-3">
            <thead><tr><th className="text-left">Taquilla</th><th className="text-right">Tickets</th><th className="text-right">Ventas</th><th className="text-right">Premios</th><th className="text-right">Balance</th></tr></thead>
            <tbody>{data.taquillas.map((t) => { const p = t.periodo[c] || { tickets: 0, ventas: 0, premios: 0, balance: 0 }; return (
              <tr key={t.id}><td className="font-mono">{t.code}</td><td className="text-right">{p.tickets}</td><td className="text-right tabular-nums">{money(c, p.ventas)}</td>
                <td className="text-right tabular-nums">{money(c, p.premios)}</td><td className="text-right tabular-nums font-semibold">{money(c, p.balance)}</td></tr>); })}
              <tr className="font-semibold" data-testid={`caja00-cuadre-total-${c}`}><td>Total agencia</td><td className="text-right">{data.kpis[c].tickets}</td>
                <td className="text-right tabular-nums">{money(c, data.kpis[c].ventas)}</td><td className="text-right tabular-nums">{money(c, data.kpis[c].pagados + data.kpis[c].por_pagar)}</td>
                <td className="text-right tabular-nums">{money(c, data.kpis[c].balance)}</td></tr></tbody>
          </table></div>
        </Card>))}
    </div>
  );
}

export default function TaquillaMadrePage({ view = "consolidado" }) {
  if (view === "tickets") return <Historial />;
  if (view === "cuadre") return <Cuadre />;
  return <Consolidado />;
}
